/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  booleanAttribute,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
} from '@angular/forms';
import { Button } from '../../button/button.component';
import { Label } from '../../label/label.component';
import { Input } from '../input.component';
import { ClientWorkspaceService, VendorWorkspaceService } from '@api-client';
import { Icon } from '../../components';
import { toObservable } from '@angular/core/rxjs-interop';

export type BusinessNumberResult = {
  businessNo: string;
  isValid: boolean;
};

@Component({
  selector: 'app-input-businessNumber',
  standalone: true,
  imports: [CommonModule, Label, FormsModule, Button, Input, Icon],
  templateUrl: './input-businessNumber.component.html',
  styleUrls: ['./input-businessNumber.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: InputBusinessNumber,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: InputBusinessNumber,
      multi: true,
    },
  ],
})
export class InputBusinessNumber
  implements ControlValueAccessor, AfterViewInit
{
  readonly changeRef = inject(ChangeDetectorRef);

  __validate = input<boolean, string | boolean>(false, {
    transform: booleanAttribute,
    alias: 'validate',
  });

  clientWorkspaceService = inject(ClientWorkspaceService);
  vendorWorkspaceService = inject(VendorWorkspaceService);
  role = input.required<'CLIENT' | 'VENDOR'>();

  label = input<string>('');
  placeholder = input<string>('');
  required = input<boolean, string>(false, { transform: booleanAttribute });
  updateMode = input(false);

  verifiedBusinessNo = output<boolean>();

  value = model<string>('');
  value$ = toObservable(this.value);
  verified = signal<boolean>(false);
  validBusinessNoPatten = signal(false);

  constructor() {
    this.value$.subscribe((value) => {
      this.clickValid.set(false);
      const formatted = this.formatBusinessNumber(value);
      this.handleValueChange(formatted);
    });
  }
  ngAfterViewInit(): void {
    if (this.updateMode()) {
      this.verified.set(true);
      this.clickValid.set(true);
      this.validBusinessNoPatten.set(true);
      this.verifiedBusinessNo.emit(true);
    }
  }

  control: AbstractControl | null = null;

  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control;
    if (!this.__validate()) return null;

    return this.verified() ? null : { unverified: true };
  }

  _validate(control: AbstractControl): ValidationErrors | null {
    this.control = control;
    if (!this.__validate()) return null;

    return this.verified() ? null : { unverified: true };
  }

  clickValid = signal<boolean>(false);

  handleReset() {
    this.value.set('');
    this.verified.set(false);
    this.clickValid.set(false);
    this.validBusinessNoPatten.set(false);
    this.verifiedBusinessNo.emit(false);
  }

  handleValid() {
    this.clickValid.set(true);
    this.verifiedBusinessNo.emit(false);
    const businessNo = this.value();

    const businessNoPattern = /^\d{3}-\d{2}-\d{5}$/;
    if (!businessNoPattern.test(businessNo)) {
      return;
    }

    this.validBusinessNoPatten.set(true);

    if (this.role() === 'VENDOR') {
      this.vendorWorkspaceService
        .vendorWorkspaceControllerValidateBusinessNo({
          businessNo: this.value(),
        })
        .subscribe({
          next: (res) => {
            if (res) {
              this.value.set(this.value());
              this.verified.set(true);
              this.control?.setErrors(null);
              this.validate(this.control!);
              this.verifiedBusinessNo.emit(true);
              return;
            }
          },
        });
    }

    if (this.role() === 'CLIENT') {
      this.clientWorkspaceService
        .clientWorkspaceControllerValidateBusinessNo({
          businessNo: this.value(),
        })
        .subscribe({
          next: (res) => {
            if (res) {
              this.value.set(this.value());
              this.verified.set(true);
              this.control?.setErrors(null);
              this.validate(this.control!);
              this.verifiedBusinessNo.emit(true);
              return;
            }
          },
        });
    }
  }

  writeValue(value: string) {
    this.value.set(value);
    this.changeRef.detectChanges();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {}

  onChange = (value: string) => {};
  onTouched = () => {};

  handleValueChange(value: string) {
    this.writeValue(value);
    this.onChange(this.value());
    this.onTouched();
  }

  formatBusinessNumber(businessNumber: string) {
    // 숫자만 남기기
    let numbers = businessNumber.replace(/\D/g, '');

    // 형식 적용: XXX-XX-XXXXX
    if (numbers.length > 10) {
      numbers = numbers.slice(0, 10); // 10자리 초과 입력 방지
    }

    if (numbers.length > 5) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5)}`;
    } else if (numbers.length > 3) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    } else {
      return numbers;
    }
  }
}
